import { ConfirmRequest } from "@/models/api/ConfirmRequest";
import { BookingDataTypeEnum, DirectionEnum } from "@/models/enums";
import bookService from "@/services/book.service";
import { useAdditionalInfoStore } from "@/store/additionalInfo";
import { useBookingStore } from "@/store/booking";
import { useCharityStore } from "@/store/charity";
import { useInsuranceStore } from "@/store/insurance";
import { useNewsletterStore } from "@/store/newsletter";
import { computed } from "vue";
import { useRoute } from "vue-router";
import useRuntimeConfig from "./useRuntimeConfig";

const useBook = () => {
    const bookingStore = useBookingStore();
    const { getRuntimeConfig } = useRuntimeConfig();
    const charityStore = useCharityStore();
    const insuranceStore = useInsuranceStore();
    const additionalInfo = useAdditionalInfoStore();
    const newsletter = useNewsletterStore();
    const route = useRoute();

    const bookingTypes = computed(() => {
        switch (route.query.step) {
            case "2":
                return [BookingDataTypeEnum.Passenger, BookingDataTypeEnum.Transfer];
            case "3":
                return [BookingDataTypeEnum.Extras, BookingDataTypeEnum.Transfer];
            case "4":
                return [BookingDataTypeEnum.Fee];
            case "5":
                return [BookingDataTypeEnum.Fee, BookingDataTypeEnum.Surcharge];
            default:
                return [];
        }
    });

    const getIdentifiersFromState = () => {
        const booking = bookingStore.getBooking;
        const identifiers: string[] = [];

        if (!booking.outward.departure.id && !booking.return.departure.id) {
            return [];
        }
				
        bookingTypes.value.forEach(type => {
            switch (type) {
                case BookingDataTypeEnum.Passenger:
                		if (booking.outward.isLocked !== true){
                    	identifiers.push(booking.outward.departure.identifier);
                    }
                    if (booking.tripType === DirectionEnum.Return) {
                        identifiers.push(booking.return.departure.identifier);
                    }
                    break;

                case BookingDataTypeEnum.Transfer:
                		if (booking.outward.isLocked !== true){
                    		booking.outward.resources.filter(x => x.code === "BUS")?.forEach(r => identifiers.push(r.identifier));
                        }
                        if (booking.tripType === DirectionEnum.Return) {
                            booking.return.resources.filter(x => x.code === "BUS")?.forEach(r => identifiers.push(r.identifier));
                        }
                    break;
                case BookingDataTypeEnum.Extras:
                		if (booking.outward.isLocked !== true){
                    		booking.outward.resources.filter(x => x.code !== "BUS")?.forEach(r => identifiers.push(r.identifier));
                        }
                        
                        if (booking.tripType === DirectionEnum.Return) {
                            booking.return.resources.filter(x => x.code !== "BUS")?.forEach(r => identifiers.push(r.identifier));
                        }
                    break;

                case BookingDataTypeEnum.Fee:
                    identifiers.push(...charityStore.getResources.map(r => r.identifier));
                    identifiers.push(...insuranceStore.getPassengers.map(p => p.identifier));
                    break;
            }
        });

        return identifiers?.filter(i => (i ?? "").length > 0);
    }

    const book = () => {
        return new Promise((resolve, reject) => {
            const identifiers = getIdentifiersFromState();
            if (identifiers.length < 1 && bookingTypes.value.includes(BookingDataTypeEnum.Passenger)) {
                reject();
            } else {
                bookService.book(identifiers, bookingTypes.value)
                    .then(resp => resolve(resp))
                    .catch(err => reject(err));
            }
        });
    }

    const confirm = () => {
    
        return new Promise((resolve, reject) => {
            bookService.confirm({
                charity: charityStore.getResources.map(r => ({
                    identifier: r.identifier,
                    giftAid: r.giftAid,
                    email: r.email
                })),
                insurancePassengers: insuranceStore.getPassengers.map(p => ({
                    ...p
                })),
                newsLetter: newsletter.getState.subscribe,
                terms: bookingStore.getBooking.terms,

                extendInsurance: insuranceStore.getExtendInsurance,
                questionnaire: {
                    wordOfMouth: newsletter.getState.referer,
                    accomodationName: additionalInfo.getState.accomodationName,
                    area: additionalInfo.getState.accomodationArea,
                    timesVisited: additionalInfo.getState.numberOfVisits
                }
            } as ConfirmRequest).then((resp) => {
                if (!resp.requiresPayment) {
                    window.location.replace(resp.redirectUrl);
                } else {
                    resolve(resp);
                }
            }).catch(err => reject(err));
        })

    }

    return { getIdentifiersFromState, book, confirm, bookingTypes }
}

export default useBook;